import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowRight, ExternalLink, ShoppingCart, Star, Users, Shield,
  CheckCircle2, Loader2, Search, Filter, Handshake, Building2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MarketplacePage() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterAge, setFilterAge] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API_URL}/api/marketplace/listings/public`, { headers })
      .then(res => {
        const data = res.data;
        setListings(Array.isArray(data) ? data : data.results || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const categories = [...new Set(listings.map(l => l.category))];
  const filtered = listings.filter(l => {
    if (filterCategory !== 'all' && l.category !== filterCategory) return false;
    if (filterAge !== 'all' && l.age_group !== filterAge && l.age_group !== 'all') return false;
    return true;
  });

  return (
    <div data-testid="marketplace-page">
      {/* Hero */}
      <section className="relative py-20 md:py-28 gradient-hero overflow-hidden">
        <div className="container-app relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 text-rsc-yellow text-sm font-bold mb-6">
            <Handshake className="w-4 h-4" /> Ready Set STEM Partner Programs
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white mb-4">
            Partner Programs
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-2">
            Expand your child's learning with trusted education partners — all accessible through 
            the Ready Set STEM platform. Math enrichment, test prep, tutoring, and more.
          </p>
          <p className="text-sm text-white/60 max-w-lg mx-auto">
            All partner programs are vetted by our team. Ready Set STEM courses are listed separately in our 
            <Link to="/catalog" className="text-rsc-yellow hover:underline ml-1">Course Catalog</Link>.
          </p>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-6 bg-white border-b">
        <div className="container-app">
          <div className="flex flex-wrap justify-center items-center gap-8 text-center text-sm">
            {[
              { icon: Shield, label: 'All Partners Vetted & Approved' },
              { icon: Star, label: 'Quality Standards Enforced' },
              { icon: Users, label: 'Read Real Parent Reviews' },
              { icon: Building2, label: 'Local & National Partners' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="flex items-center gap-2 text-gray-600">
                  <Icon className="w-4 h-4 text-rsc-blue" />
                  <span>{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-gray-50 border-b">
        <div className="container-app flex flex-wrap items-center gap-4">
          <span className="text-sm font-semibold text-gray-700"><Filter className="w-4 h-4 inline mr-1" />Filter:</span>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterAge} onValueChange={setFilterAge}>
            <SelectTrigger className="w-[160px]"><SelectValue placeholder="All Ages" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ages</SelectItem>
              <SelectItem value="young">Ages 5-9</SelectItem>
              <SelectItem value="tween">Ages 10-13</SelectItem>
              <SelectItem value="teen">Ages 14-18</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-500 ml-auto">{filtered.length} program{filtered.length !== 1 ? 's' : ''} found</span>
        </div>
      </section>

      {/* Listings */}
      <section className="section-padding">
        <div className="container-app">
          {loading ? (
            <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No partner programs match your filters.</p>
              <Button variant="outline" className="mt-4" onClick={() => { setFilterCategory('all'); setFilterAge('all'); }}>Clear Filters</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((listing, i) => (
                <Card key={listing.id} className="card-marketing h-full overflow-hidden hover:shadow-lg transition-shadow">
                  <div className={`h-1.5 ${listing.listing_type === 'referral' ? 'bg-blue-500' : listing.listing_type === 'embedded' ? 'bg-green-500' : 'bg-purple-500'}`} />
                  <CardContent className="p-6">
                    {/* Partner badge */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {listing.partner_logo ? (
                          <img src={listing.partner_logo} alt="" className="h-6 w-auto" />
                        ) : (
                          <Building2 className="w-5 h-5 text-gray-400" />
                        )}
                        <span className="text-xs font-semibold text-gray-500">{listing.partner_name}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {listing.listing_type === 'referral' ? 'Partner Site' : 'Enroll Here'}
                      </Badge>
                    </div>

                    {/* Content */}
                    <h3 className="font-heading text-lg font-bold text-rsc-navy mb-2">{listing.title}</h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                      {listing.short_description || listing.description}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      <Badge className="bg-gray-100 text-gray-600 text-xs">{listing.category}</Badge>
                      {listing.grade_range && <Badge variant="outline" className="text-xs">{listing.grade_range}</Badge>}
                      {listing.location_description && (
                        <Badge variant="outline" className="text-xs">📍 {listing.location_description}</Badge>
                      )}
                    </div>

                    {/* Highlights */}
                    {listing.highlights && listing.highlights.length > 0 && (
                      <div className="space-y-1 mb-4">
                        {listing.highlights.slice(0, 3).map((h, hi) => (
                          <div key={hi} className="flex items-center gap-1.5 text-xs text-gray-600">
                            <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />
                            <span>{h}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Schedule */}
                    {listing.schedule_description && (
                      <p className="text-xs text-gray-500 mb-4">🕐 {listing.schedule_description}</p>
                    )}

                    {/* Pricing & CTA */}
                    <div className="flex items-center justify-between pt-3 border-t">
                      <div>
                        {listing.original_price && (
                          <span className="text-sm text-gray-400 line-through mr-2">${parseFloat(listing.original_price).toFixed(0)}</span>
                        )}
                        <span className="text-xl font-black text-rsc-navy">${parseFloat(listing.price).toFixed(0)}</span>
                        <span className="text-xs text-gray-500">/{listing.price_period?.replace('per_', '')}</span>
                      </div>
                      {listing.listing_type === 'referral' ? (
                        <a href={listing.referral_url} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="text-xs">
                            Visit Site <ExternalLink className="ml-1 h-3 w-3" />
                          </Button>
                        </a>
                      ) : (
                        <Link to="/contact">
                          <Button size="sm" className="btn-primary text-xs">
                            Enroll <ShoppingCart className="ml-1 h-3 w-3" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Become a Partner CTA */}
      <section className="section-padding bg-rsc-navy text-white">
        <div className="container-app text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Are You an Education Provider?</h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            List your programs on the Ready Set STEM marketplace and reach 12,000+ families. 
            Referral, embedded enrollment, or white-label partnerships available.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact"><Button className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright text-lg px-8 py-6 font-bold rounded-full">Become a Partner <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
